import type {
  ETreatmentPlanJobState,
  ETreatmentPlanJobSystemSignal,
  ETreatmentPlanJobUiControlType,
} from "../../treatmentPlan/job";
import type { TInsuranceCompany } from "@/api/models/insuranceCompany/insuranceCompany";
import type { ESex, TPracticeStaff } from "@/api/models/practice/practiceStaff";
import type { TEmail, TPhone } from "@/api/shared/contacts";
import type { TAddress } from "@/api/shared/address";
import type { TFile } from "@/api/shared/file";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import type { TCompany } from "@/api/models/company/company";
import type { TPractice } from "@/api/models/practice/practice";
import type { TTag } from "@/api/shared/tag";
import type { ETreatmentType } from "@/api/models/treatmentPlan/treatmentPlan";
import type { TProviderKindKey } from "../linkEntity";
import type { EConsultationStatus } from "@/api/models/referral/consultation";
import { GuardianTypeEnum as EGuardianRelationship } from "@/api/generated/patient/patient.types";

export { EGuardianRelationship };

/** Not in OpenAPI schema anymore — kept for frontend until API restores the field. */
export enum EPatientInterestState {
  INTERESTED = "interested",
  FOLLOW_UP_NEEDED = "follow_up_needed",
  CONSULT_SCHEDULED = "consult_scheduled",
  PENDING_DECISION = "pending_decision",
  TREATMENT_ACCEPTED = "treatment_accepted",
  DECLINED_AFTER_INFO = "declined_after_info",
  ASK_ME_NEXT = "ask_me_next",
  NOT_INTERESTED = "not_interested",
  IN_TREATMENT = "in_treatment",
}

export enum EReferralType {
  IN = "in",
  OUT = "out",
}

export enum EPatientAccessType {
  OWNER = "owner",
  REFERRAL_RECEIVER = "referral_receiver",
}

export enum EPatientFlow {
  FILL_ORTHO_NEEDS = 1,
  WAITING_FOR_TC_APPROVAL = 2,
  FILL_TREATMENT_PLAN = 3,
  WAITING_FOR_PATIENT_SIGNATURE = 4,
  WAITING_FOR_NEXT_VISIT_INFO = 5,
  COMPLETE_CHECKLIST = 6,
  IN_TREATMENT = 7,
}

export interface TJobDialogMetaData {
  title?: string;
  subtitle?: string;
  cancelText?: string;
  acceptText?: string;
}

export interface TJobMetaData {
  labels?: {
    en: {
      [key in ETreatmentPlanJobState]?: {
        jobTitle?: string;
        dialog?: TJobDialogMetaData;
        errorMessage?: string;
        successMessage?: string;
        nextInstruction?: string;
      };
    };
    graph?: { position: { x: number; y: number } };
  };
}

export interface TJobsAvailableForUser {
  meta_data?: TJobMetaData;
  id: string;
  state: ETreatmentPlanJobState;
  name: string;
  next_instruction: string;
  phase: EPatientFlow;
  signal_system_type?: ETreatmentPlanJobSystemSignal;
  signal_ui_control_type?: ETreatmentPlanJobUiControlType;
}

export type TPatientReferralEmbedded = {
  id: string;
  created_at: string;
  status: string;
  status_changed_at?: string | null;
};

export interface TPatientTreatmentPlan {
  id: string;

  state: ETreatmentPlanJobState;
  actual_phase: EPatientFlow;

  order_aligners: string[];
  financing_plans: string[];
  schedule_tc_appointments: string[];
  jobs_available_for_user: TJobsAvailableForUser[];

  ortho_needs: string[];
  ortho_proposed_plans: string[];
  aligner_delivery_instructions: string[];
  next_visit_instructions_v2: string[];
  final_debond_visit_instructions: string[];
  // it is not on BE idk how the fuck it works
  referral?: TPatientReferralEmbedded;
}

/**
 * Demographics shared by Blob patients, related people, and PMS import persons.
 * All fields optional — consumers tighten requiredness (e.g. TPatient).
 */
export interface TPersonCore {
  first_name?: string;
  last_name?: string;
  middle_name?: string | null;
  preferred_name?: string | null;
  sex?: string | null;
  birth_date?: Date | string | null;
  emails?: TEmail[] | null;
  phones?: TPhone[] | null;
  addresses?: TAddress[] | null;
}

/**
 * External PMS identity shared by patients and related people.
 *
 * The PMS may know a record as a patient, as a person, or as both, so the two ids are
 * independent and either can be absent.
 */
export interface TPmsLink {
  pms_patient_id?: string;
  pms_person_id?: string;
  pms_platform?: string;
}

/** Blob patient demographics: person core with required identity/contact fields. */
export type TPatientDemographics = Required<
  Pick<
    TPersonCore,
    | "first_name"
    | "last_name"
    | "middle_name"
    | "preferred_name"
    | "birth_date"
    | "emails"
    | "phones"
    | "addresses"
  >
> & {
  /** Blob patients always have a concrete sex value (empty string when unknown). */
  sex: string;
};

export interface TPatient extends TPatientDemographics, TPmsLink {
  id: string;
  created_at: string;

  is_patient?: boolean;

  language?: string | null;
  language_2?: string | null;

  insurances?: TPatientInsurance[];
  consents?: TPatientConsent[];

  medical_fin_number?: string;
  previous_ortho_treatment?: boolean;

  picture?: TFile | null;
  picture_default: string;

  guardians?: TGuardian[];
  guarantor?: TRelatedPerson | null;
  insurance_subscribers?: TRelatedPerson[];

  actual_treatment_plans: TPatientTreatmentPlan[];

  practices_linked: TLinkedPractice[];
  companies_linked: TLinkedCompany[];
  staff_linked: TLinkedStaff[];
  staff_created_by: TPracticeStaff;

  dm_app_activated: boolean;
  confirmed_at: string | null;

  smile_concerns?: string;
  tags: TTag[];

  hrid: string;

  // Additional API fields not shown in the provided Swagger schema.
  interest_state?: EPatientInterestState;
  interest_reason?: string;
  treatment?: TPatientTreatment;
  status?: TPatientStatusInfo;
  access_type?: EPatientAccessType | null;

  // Frontend-only fields.
  guardianConsent: boolean;
  practice_id?: string;
  user_permissions?: string[];
}

export type TPatientStatusInfo = {
  status: EPatientStatus;
  note?: string;
};

export enum EPatientStatus {
  // Referrals In
  NEW = "new",
  CONTACT_ATTEMPTED_NO_RESPONSE = "contact_attempted_no_response",
  MULTIPLE_ATTEMPTS_NO_RESPONSE = "multiple_attempts_no_response",
  CONTACTED_REQUESTED_CALLBACK = "contacted_requested_callback",
  CONSULTATION_SCHEDULED = "consultation_scheduled",
  CONSULTATION_RESCHEDULED = "consultation_rescheduled",
  CONSULTATION_CANCELED = "consultation_canceled",
  CONSULTATION_NO_SHOW = "consultation_no_show",
  CONSULTATION_COMPLETED = "consultation_completed",

  // Co-Treated / Contract
  DECLINED_TREATMENT = "declined_treatment",
  OBSERVATION_NOT_READY = "observation_not_ready",
  PENDING_START_NEEDED = "pending_start_needed",
  CONTRACT_STARTED = "contract_started",
  DOCTOR_DECLINED_TREATMENT = "doctor_declined_treatment",
}

export type TLinkedRoles = "gp" | "ortho" | "tc" | "";

export interface TLinkedStaff {
  staff: TPracticeStaff;
  link_type: TLinkedRoles;
}

export interface TLinkedPractice {
  practice: TPractice;
  link_type: TLinkedRoles;
}

export interface TLinkedCompany {
  company: TCompany;
  link_type: TLinkedRoles;
}

export interface TPatientTreatment {
  treatment_type: ETreatmentType;
  treatment_data?: {
    link_ortho_practice: string;
    link_gp_practice: string;
  };
}

export interface TAllAvailablePractices {
  gp_practices: TPractice[];
  ortho_practices: TPractice[];
}

export interface TRelatedPerson extends TPersonCore, TPmsLink {
  id?: string;
  /** Relationship label when this person is a guardian (or similar). */
  type?: string;
}

/** Inline person shape from API; legacy `guardian` / `guardian_data` kept for older UI paths. */
export interface TGuardian extends TRelatedPerson {
  type?: string;
  /** @deprecated Link by `id` instead. */
  guardian?: string;
  /** @deprecated Person fields are inline on the guardian object. */
  guardian_data?: TGuardianData;
  /** @deprecated Payer is expressed via `guarantor`. */
  is_payer?: boolean;
}

export interface TGuardianData {
  first_name?: string;
  last_name?: string;
  birth_date?: string;
  sex?: ESex;
  emails?: TEmail[];
  phones?: TPhone[];
  addresses?: TAddress[];
  insurances?: TPatientInsurance[];
  picture?: TFile | null;
}

export interface TPatientInsurance {
  id?: string;
  is_active?: boolean;
  insurance_company: number | null;
  insurance_company_data?: TInsuranceCompany | null;
  number: string | null;
  start: Date | string | null;
  end: Date | string | null;
}

export interface TPatientConsent {
  consent: string;
}

export interface TPatientConsentTypes {
  id: string;
  name: string;
  description: string;
  created_at: Date;
}

export interface TWithRecaptchaToken {
  recaptcha_token?: string;
}

export type TPatientPublicPayload = Partial<TPatient> & TWithRecaptchaToken;

/**
 * Response of `POST patient/public/` — carries the referral/consultation-job pair that
 * authorizes the anonymous self-scheduling flow, and whether it's offered at all (only
 * `true` when the receiving practice has an active Cloud9 key). See
 * `c9_support_docs/document.md` §3 and `demo_patient_self_scheduling.py`.
 */
export type TPatientPublicRegistrationResponse = TPatient & {
  referral_id?: string;
  job_id?: string;
  self_scheduling_available?: boolean;
};

export interface TPatientSearchFilters extends DefaultPaginatedFilters {
  phase__in?: number[];
  flow__in?: number[];
  actual_treatment_plans__actual_phase__in?: number[];
  filter_action_for_my_patients?: boolean | null;
  actual_treatment_plans__state?: string;
  my_referrals?: number;
  referral_type?: EReferralType;
  /** Latest consultation status (referrals-in / referrals-out dashboard). */
  consultation_status?: EConsultationStatus;
  type: EPatientType;
  unverified?: number;
  linked_entity_type?: TProviderKindKey;
  qr_usages?: string;
  ordering?: "last_name" | "-last_name";
  exclude_id__in?: string[];
  exclude_minors?: boolean;
}

export enum EPatientType {
  GUARDIAN = "guardian",
  PATIENT = "patient",
}

type AvailablePlansParams = { patientId?: string };

export interface IAvailablePlans extends DefaultPaginatedFilters, AvailablePlansParams {}
